/* eslint-disable max-lines */
import { format, isToday, isYesterday } from 'date-fns';
import { FolderItem, Song } from 'types/song';
import { ContextMenuOption, SortOrder } from 'types/general.types';
import { getOpenInNewTabMenuOption } from 'app/components/ChakraTableList';
import { routes } from 'app/routing/routesConfig';
import { getSiteLocalSettings } from 'app/utils/hooks/useResizableBlock';
import { SortParams } from 'types/generic.types';
import uniq from 'lodash/uniq';
import { AppDispatch } from 'app/store';
import { pinSongEffect, unpinSongEffect } from 'app/store/SongList/effects';
import { AVATAR_SIZE, OPTIONS_SIZE } from './constants';

const sortSongsBy = ['title', 'status', 'updated', 'created'] as const;
export type SongsSortBy = typeof sortSongsBy[number];

export const isSongsSortBy = (sortBy: string | null): boolean =>
  sortBy ? sortSongsBy.includes(sortBy as SongsSortBy) : false;

export const getFormattedModifiedDate = (iso: string) => {
  const songDate = new Date(iso);
  if (isToday(songDate)) {
    return `Today - ${format(songDate, 'HH:mm')}`;
  }
  if (isYesterday(songDate)) {
    return `Yesterday - ${format(songDate, 'HH:mm')}`;
  }
  return format(songDate, 'dd LLL yyyy - HH:mm');
};

export const getSongListMenuOptions: (
  song: Song,
  setLeaveSong: (song?: Song) => void,
  isPinned?: boolean,
  dispatch?: AppDispatch,
  folder?: FolderItem,
  selectedRowsIds?: string[],
  setShowRemoveFromFolderDialog?: (props: {
    folder: FolderItem;
    song: Song;
    selectedRowsIds: string[];
  }) => void
) => ContextMenuOption[] = (
  song,
  setLeaveSong,
  isPinned = false,
  dispatch,
  folder,
  selectedRowsIds = [],
  setShowRemoveFromFolderDialog
) => {
  if (song._links?.leaveSong) {
    return [
      ...getOpenInNewTabMenuOption(
        routes.song.path.replace(':guid', song.guid)
      ),
      ...(folder && setShowRemoveFromFolderDialog
        ? [
            {
              title: `Remove ${
                selectedRowsIds.length > 0 ? 'Selected' : ''
              } from Song Folder`,
              value: 'REMOVE_FROM_FOLDER',
              onClick: () => {
                setShowRemoveFromFolderDialog({
                  folder,
                  song,
                  selectedRowsIds,
                });
              },
            },
          ]
        : [
            {
              title: isPinned ? 'Unpin Song' : 'Pin Song',
              value: isPinned ? 'UNPIN_SONG' : 'PIN_SONG',
              onClick: () => {
                if (isPinned) {
                  dispatch?.(
                    unpinSongEffect({ guid: song.guid, title: song.title })
                  );
                } else {
                  dispatch?.(
                    pinSongEffect({ guid: song.guid, title: song.title })
                  );
                }
              },
            },
          ]),
      {
        title: 'Leave Song',
        value: 'LEAVE',
        onClick: () => {
          setLeaveSong(song);
        },
      },
    ];
  }
  return [];
};

export const getInitialSortParams = (
  key?: 'songs' | string
): {
  sortBy: SongsSortBy;
  sortOrder: SortOrder;
} =>
  (key && getSiteLocalSettings()?.[key]) || {
    sortBy: 'updated',
    sortOrder: 'desc',
  };

export const getDragPreviewSubTitle = (songGuids: string[]) => {
  if (songGuids.length === 1) {
    return null;
  }

  return `+${songGuids.length} songs`;
};

const getColumnSize = (percent: number, totalWidth?: number) => {
  if (!totalWidth) {
    return undefined;
  }
  return (totalWidth - AVATAR_SIZE - OPTIONS_SIZE) * percent;
};

export const getColumns = ({
  loading,
  sortParams,
  totalWidth,
}: {
  loading: boolean;
  sortParams: SortParams<SongsSortBy>;
  totalWidth?: number;
}) => [
  {
    accessorKey: 'accessory',
    size: AVATAR_SIZE,
    isAvatar: true,
  },
  {
    accessorKey: 'title',
    header: 'Title',
    minSize: 62,
    sx: { pl: 0 },
    size: getColumnSize(0.25, totalWidth),
    enableResizing: true,
    sortColumnProps: {
      column: 'title',
      sortParams,
      initSortOrder: 'desc' as SortOrder,
      disabled: loading,
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: getColumnSize(0.25, totalWidth),
    minSize: 86,
    enableResizing: true,
    sortColumnProps: {
      column: 'status',
      sortParams,
      initSortOrder: 'desc' as SortOrder,
      disabled: loading,
    },
  },
  {
    accessorKey: 'updated',
    header: 'Last Modified',
    size: getColumnSize(0.25, totalWidth),
    minSize: 135,
    enableResizing: true,
    sortColumnProps: {
      column: 'updated',
      sortParams,
      initSortOrder: 'desc' as SortOrder,
      disabled: loading,
    },
  },
  {
    accessorKey: 'created',
    header: 'Created',
    size: getColumnSize(0.25, totalWidth),
    sortColumnProps: {
      column: 'created',
      sortParams,
      initSortOrder: 'desc' as SortOrder,
      disabled: loading,
    },
  },
  {
    accessorKey: 'gap',
    size: 0,
  },
  {
    accessorKey: 'options',
    size: OPTIONS_SIZE,
    isOptions: true,
  },
];

export const getNewSelectedRowsIds = ({
  songGuid,
  shift,
  songs,
  selectedRowsIds,
}: {
  songGuid: string;
  shift: boolean;
  songs: Song[];
  selectedRowsIds: string[];
}) => {
  const clickedSongIndex = songs.findIndex(({ guid }) => guid === songGuid);
  const lastSelectedSongIndex = shift
    ? songs.findIndex(
        ({ guid }) => guid === selectedRowsIds[selectedRowsIds.length - 1]
      )
    : -1;

  const selectedSongGuids = selectedRowsIds.filter(
    (selectedSongGuid) => selectedSongGuid !== songGuid
  );

  if (selectedSongGuids.length !== selectedRowsIds.length) {
    return selectedSongGuids;
  }

  const songGuids =
    lastSelectedSongIndex === -1
      ? [songGuid]
      : songs
          .slice(
            Math.min(lastSelectedSongIndex, clickedSongIndex),
            Math.max(lastSelectedSongIndex, clickedSongIndex) + 1
          )
          .map(({ guid }) => guid);

  return uniq([...selectedRowsIds, ...songGuids]);
};

export const getFolderLocalStorageKey = (folder?: FolderItem) =>
  folder?.guid ? `folder-${folder.guid}` : 'songs';
